import type { TFunction } from "i18next";
import type {
	GetMigrationImportQuestionsQuery,
	GetMigrationReviewQuery,
	MigrationPreparedRowFragment
} from "~/gql/queries/onboardingJourney.generated";
import type { ChatModule } from "~/services/aiOrchestrator";

import {
	MigrationApprovalKind,
	MigrationPreparationJobState,
	OnboardingJourneyOperationStatus,
	OnboardingJourneyQuestionState
} from "~/gql/graphqlBase.generated";
import onboardingWelcome from "~/helpers/onboardingWelcome";

type PreparationJob = GetMigrationReviewQuery["migrationPreparationJobs"][number];
type JourneyState = NonNullable<GetMigrationReviewQuery["onboardingJourneyState"]>;
type Portion = JourneyState["progress"]["portions"][number];

type ImportQuestionPage = GetMigrationImportQuestionsQuery["migrationImportQuestions"];

export type ImportQuestion = ImportQuestionPage["questions"][number];

export interface PreparationCard {
	blockingQuestions: number;
	fileName: string | null;
	hasImportRun: boolean;
	jobId: string;
	labelKey: string;
	rowsPrepared: number;
	rowsRead: number;
	sourceId: string;
	state: MigrationPreparationJobState;
}

export interface FamilyCount {
	count: number;
	family: string;
}

export type ReviewApproval = "approved" | "notShownToYou" | "waitingForYou";

export interface ReviewSet {
	approval: ReviewApproval;
	families: FamilyCount[];
	fileName: string | null;
	isStale: boolean;
	kind: "data" | "settings";
	latestRevision: number | null;
	operationCount: number;
	presentedAt: string | null;
	revision: number | null;
	sourceId: string | null;
	subjectId: string;
}

export interface ReviewSetCopy {
	hintKey: string;
	titleKey: string;
}

export interface MigrationReviewView {
	hasPendingAction: boolean;
	isPreparing: boolean;
	openQuestionCount: number;
	preparationCards: PreparationCard[];
	reviewSets: ReviewSet[];
	unfinishedElsewhere: FamilyCount[];
	waitingCount: number;
}

export type QuestionStatus = "answered" | "earlierAnswer" | "open";

export type OpensOnboardingEvent = "journeyStartedFromFiles" | "newChat" | "openedChat" | "sent";

export interface GroupedAnswerCoverage {
	covered: number;
	exceptions: number;
}

export interface InspectionRow {
	id: string;
	sourceRows: string;
	values: Record<string, string>;
}

const PREPARATION_POLL_MS = 5000;

const ONBOARDING_MODULE: ChatModule = "ONBOARDING";

const INSPECTION_PAGE_SIZE = 100;

const QUESTION_PAGE_SIZE = 100;

const MAX_CELL_LENGTH = 500;

const HIDDEN_CHARACTERS = /[\p{Cc}\p{Cf}]/gu;

const PREPARATION_LABEL_KEY: Record<MigrationPreparationJobState, string | null> = {
	[MigrationPreparationJobState.Failed]: "fernieChat.review.preparation.couldNotPrepare",
	[MigrationPreparationJobState.NeedsAnswers]: "fernieChat.review.preparation.needsAnswers",
	[MigrationPreparationJobState.Pending]: "fernieChat.review.preparation.waitingToStart",
	[MigrationPreparationJobState.Prepared]: "fernieChat.review.preparation.readyToReview",
	[MigrationPreparationJobState.Running]: "fernieChat.review.preparation.preparing",
	[MigrationPreparationJobState.Superseded]: null
};

const PREPARING_STATES: ReadonlySet<MigrationPreparationJobState> = new Set([
	MigrationPreparationJobState.Pending,
	MigrationPreparationJobState.Running
]);

const isPreparingState = (state: MigrationPreparationJobState): boolean => PREPARING_STATES.has(state);

const SET_TITLE_KEY: Record<ReviewSet["kind"], string> = {
	data: "fernieChat.review.set.dataTitle",
	settings: "fernieChat.review.set.settingsTitle"
};

const APPROVAL_ORDER: Record<ReviewApproval, number> = { approved: 2, notShownToYou: 1, waitingForYou: 0 };

const EMPTY_VIEW: MigrationReviewView = {
	hasPendingAction: false,
	isPreparing: false,
	openQuestionCount: 0,
	preparationCards: [],
	reviewSets: [],
	unfinishedElsewhere: [],
	waitingCount: 0
};

const fileNameOf = (review: GetMigrationReviewQuery, sourceId: string, sourceVersion: number): string | null =>
	(review.tenant?.onboardingUploads ?? []).find(
		(upload) => upload.sourceId === sourceId && upload.sourceVersion === sourceVersion
	)?.fileName ?? null;

const isNewestVersion = (job: PreparationJob, jobs: readonly PreparationJob[]): boolean =>
	jobs.every((other) => other.sourceId !== job.sourceId || other.sourceVersion <= job.sourceVersion);

const isCurrentJob = (job: PreparationJob, jobs: readonly PreparationJob[]): boolean =>
	job.state !== MigrationPreparationJobState.Superseded && isNewestVersion(job, jobs);

const buildPreparationCards = (review: GetMigrationReviewQuery): PreparationCard[] =>
	review.migrationPreparationJobs.flatMap((job) => {
		const labelKey = PREPARATION_LABEL_KEY[job.state];
		if (!labelKey || !isCurrentJob(job, review.migrationPreparationJobs)) return [];
		return [
			{
				blockingQuestions: job.blockingQuestions,
				fileName: fileNameOf(review, job.sourceId, job.sourceVersion),
				hasImportRun: review.migrationImportRuns.some((run) => run.sourceId === job.sourceId),
				jobId: job.jobId,
				labelKey,
				rowsPrepared: job.rowsPrepared,
				rowsRead: job.rowsRead,
				sourceId: job.sourceId,
				state: job.state
			}
		];
	});

const familyListText = (families: readonly FamilyCount[], t: TFunction): string =>
	families
		.map(({ count, family }) => {
			const labelKey = onboardingWelcome.familyLabelKey(family);
			const label = labelKey ? t(labelKey) : inertCellText(family);
			return t("fernieChat.review.familyCount", { count, family: label });
		})
		.join(", ");

const awaitsAnswers = (card: PreparationCard): boolean =>
	card.state === MigrationPreparationJobState.NeedsAnswers || card.blockingQuestions > 0;

const countByFamily = (operations: readonly { family: string }[]): FamilyCount[] => {
	const counts = new Map<string, number>();
	for (const { family } of operations) counts.set(family, (counts.get(family) ?? 0) + 1);
	return [...counts].map(([family, count]) => ({ count, family }));
};

const approvalOf = (portion: Portion, isPresentedToYou: boolean): ReviewApproval | null => {
	if (portion.run) return "approved";
	if (isPresentedToYou) return "waitingForYou";
	const awaitsApproval = portion.operations.some(
		(operation) => operation.status === OnboardingJourneyOperationStatus.AwaitingApproval
	);
	return awaitsApproval ? "notShownToYou" : null;
};

const buildDataSet = (portion: Portion, review: GetMigrationReviewQuery, state: JourneyState): ReviewSet | null => {
	const presentation = review.migrationPendingReviews.find(
		(pendingReview) =>
			pendingReview.kind === MigrationApprovalKind.DataPortion && pendingReview.subjectId === portion.portionId
	);
	const approval = approvalOf(portion, presentation !== undefined);
	if (!approval) return null;
	const revisions = state.artifacts.filter((artifact) => artifact.artifactId === portion.artifactId);
	const latestRevision = Math.max(portion.artifactRevision, ...revisions.map((artifact) => artifact.revision));
	const jobId = revisions.find((artifact) => artifact.revision === portion.artifactRevision)?.jobId;
	const job = review.migrationPreparationJobs.find((candidate) => candidate.jobId === jobId);
	const isJobReplaced = job !== undefined && !isCurrentJob(job, review.migrationPreparationJobs);
	return {
		approval,
		families: countByFamily(portion.operations),
		fileName: job ? fileNameOf(review, job.sourceId, job.sourceVersion) : null,
		isStale: approval !== "approved" && (latestRevision > portion.artifactRevision || isJobReplaced),
		kind: "data",
		latestRevision,
		operationCount: portion.operations.length,
		presentedAt: presentation?.presentedAt ?? null,
		revision: portion.artifactRevision,
		sourceId: job?.sourceId ?? null,
		subjectId: portion.portionId
	};
};

const presentedWithoutProgress = (review: GetMigrationReviewQuery, portions: readonly Portion[]): ReviewSet[] =>
	review.migrationPendingReviews
		.filter(
			(pendingReview) =>
				pendingReview.kind === MigrationApprovalKind.SettingsRevision ||
				(pendingReview.kind === MigrationApprovalKind.DataPortion &&
					!portions.some((portion) => portion.portionId === pendingReview.subjectId))
		)
		.map((pendingReview) => ({
			approval: "waitingForYou",
			families: [],
			fileName: null,
			isStale: false,
			kind: pendingReview.kind === MigrationApprovalKind.SettingsRevision ? "settings" : "data",
			latestRevision: null,
			operationCount: 0,
			presentedAt: pendingReview.presentedAt,
			revision: null,
			sourceId: null,
			subjectId: pendingReview.subjectId
		}));

const buildReviewSets = (review: GetMigrationReviewQuery, state: JourneyState): ReviewSet[] => {
	const { portions } = state.progress;
	const dataSets = portions.flatMap((portion) => buildDataSet(portion, review, state) ?? []);
	return [...dataSets, ...presentedWithoutProgress(review, portions)].sort(
		(a, b) => APPROVAL_ORDER[a.approval] - APPROVAL_ORDER[b.approval]
	);
};

const buildReview = (review: GetMigrationReviewQuery | undefined, journeyId: string | null): MigrationReviewView => {
	const state = review?.onboardingJourneyState;
	if (!review || !state || state.journeyId !== journeyId) return EMPTY_VIEW;
	const preparationCards = buildPreparationCards(review);
	const reviewSets = buildReviewSets(review, state);
	const waitingSets = reviewSets.filter((set) => set.approval === "waitingForYou");
	const waitingIds = new Set(waitingSets.map((set) => set.subjectId));
	const openQuestionCount = state.questions.filter(
		(question) => question.state === OnboardingJourneyQuestionState.Open
	).length;
	return {
		hasPendingAction: waitingSets.length > 0 || openQuestionCount > 0 || preparationCards.some(awaitsAnswers),
		isPreparing: preparationCards.some((card) => isPreparingState(card.state)),
		openQuestionCount,
		preparationCards,
		reviewSets,
		unfinishedElsewhere: countByFamily(
			state.progress.remainingWork.filter((operation) => !waitingIds.has(operation.portionId))
		),
		waitingCount: waitingSets.length
	};
};

const questionStatus = (question: ImportQuestion): QuestionStatus => {
	if (!question.open) return "answered";
	return question.answer && !question.answer.applicable ? "earlierAnswer" : "open";
};

const answerLabel = (question: ImportQuestion): string | null => {
	const option = question.answer?.option;
	if (option === undefined) return null;
	return question.options.find((candidate) => candidate.key === option)?.label ?? option;
};

const groupedAnswerCoverage = (question: ImportQuestion): GroupedAnswerCoverage => {
	const exceptions = question.answer?.exceptions.length ?? 0;
	return { covered: Math.max(question.affectedCount - exceptions, 0), exceptions };
};

const currentQuestions = (questions: readonly ImportQuestion[]): ImportQuestion[] =>
	questions.filter((question) => question.current);

const showsQuestions = (page: ImportQuestionPage): boolean =>
	currentQuestions(page.questions).length > 0 || page.total > page.questions.length;

const questionPageArgs = (journeyId: string, sourceId: string, skip: number) => ({
	journeyId,
	openOnly: false,
	skip,
	sourceId,
	take: QUESTION_PAGE_SIZE
});

const inertCellText = (text: string): string => {
	const flattened = text.replace(/\s+/g, " ").replace(HIDDEN_CHARACTERS, "").trim();
	return flattened.length > MAX_CELL_LENGTH ? `${flattened.slice(0, MAX_CELL_LENGTH)}…` : flattened;
};

const inspectionFieldNames = (rows: readonly MigrationPreparedRowFragment[]): string[] => [
	...new Set(rows.flatMap((row) => row.fields.map((field) => field.name)))
];

const toInspectionRows = (rows: readonly MigrationPreparedRowFragment[]): InspectionRow[] =>
	rows.map((row) => ({
		id: row.row,
		sourceRows: row.sourceRows.join(", "),
		values: Object.fromEntries(row.fields.map((field) => [field.name, field.value]))
	}));

const reviewSetCopy = (set: ReviewSet): ReviewSetCopy => {
	if (set.approval === "approved")
		return { hintKey: "fernieChat.review.set.approvedHint", titleKey: "fernieChat.review.set.approvedTitle" };
	if (set.approval === "notShownToYou")
		return { hintKey: "fernieChat.review.set.notShownHint", titleKey: "fernieChat.review.set.notShownTitle" };
	if (set.isStale) return { hintKey: "fernieChat.review.set.stale", titleKey: SET_TITLE_KEY[set.kind] };
	if (set.kind === "settings")
		return { hintKey: "fernieChat.review.set.settingsHint", titleKey: SET_TITLE_KEY.settings };
	const hintKey =
		set.families.length > 0 ? "fernieChat.review.set.howToApprove" : "fernieChat.review.set.detailsPending";
	return { hintKey, titleKey: SET_TITLE_KEY.data };
};

const exampleText = (row: MigrationPreparedRowFragment): string =>
	inertCellText(row.fields.map((field) => `${field.name}: ${field.value}`).join(", "));

const turnModule = ({
	dockModule,
	opensOnboarding,
	showsPendingAction
}: {
	dockModule: ChatModule | undefined;
	opensOnboarding: boolean;
	showsPendingAction: boolean;
}): ChatModule | undefined => (opensOnboarding || showsPendingAction ? ONBOARDING_MODULE : dockModule);

const turnInputs = (
	review: Pick<MigrationReviewView, "hasPendingAction">,
	sendOptions?: { opensOnboarding?: boolean; opensOnboardingNext?: boolean }
): { opensOnboarding: boolean; showsPendingAction: boolean } => ({
	opensOnboarding: (sendOptions?.opensOnboarding ?? false) || (sendOptions?.opensOnboardingNext ?? false),
	showsPendingAction: review.hasPendingAction
});

const nextOpensOnboarding = (_current: boolean, event: OpensOnboardingEvent): boolean =>
	event === "journeyStartedFromFiles";

const migrationReview = {
	answerLabel,
	buildReview,
	currentQuestions,
	exampleText,
	familyListText,
	groupedAnswerCoverage,
	inertCellText,
	inspectionFieldNames,
	inspectionPageSize: INSPECTION_PAGE_SIZE,
	isPreparingState,
	nextOpensOnboarding,
	preparationPollMs: PREPARATION_POLL_MS,
	questionPageArgs,
	questionPageSize: QUESTION_PAGE_SIZE,
	questionStatus,
	reviewSetCopy,
	showsQuestions,
	toInspectionRows,
	turnInputs,
	turnModule
};

export default migrationReview;
